import { ErrorPage } from "@components/pages/ErrorPage"
import { SiteShell } from "@components/SiteShell"
import { localeFromPathname, publicAssetBaseUrl } from "@lib/meta"
import type { QueryClient } from "@tanstack/react-query"
import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  Scripts,
  useRouterState,
} from "@tanstack/react-router"
import type { ReactNode } from "react"
import appCss from "../../apps/site/src/global.css?url"
import { analyticsScript, remoteImageSkeletonScript, viewTransitionRouterScript } from "../rootScripts"

const fontAwesomeKitUrl = "https://kit.fontawesome.com/70d7c74973.css"
const anonymousCrossOrigin = "anonymous" as const

export const Route = createRootRouteWithContext<{
  queryClient: QueryClient
}>()({
  head: () => ({
    title: "PLAYBRAIN Inc.",
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width" },
      { name: "generator", content: "TanStack Start" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "stylesheet", href: fontAwesomeKitUrl },
      { rel: "sitemap", href: "/sitemap.xml" },
      { rel: "icon", type: "image/x-icon", href: "/favicon.png" },
      { rel: "preconnect", href: "https://use.typekit.net", crossOrigin: anonymousCrossOrigin },
      { rel: "preconnect", href: "https://p.typekit.net", crossOrigin: anonymousCrossOrigin },
      { rel: "preconnect", href: publicAssetBaseUrl(), crossOrigin: anonymousCrossOrigin },
    ],
  }),
  component: RootComponent,
  notFoundComponent: NotFound,
})

function RootComponent() {
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  return (
    <RootDocument
      locale={localeFromPathname(pathname)}
      pageClass={pageClassFromPathname(pathname)}
    >
      <Outlet />
    </RootDocument>
  )
}

function pageClassFromPathname(pathname: string) {
  const segments = pathname.split("/").filter(Boolean)
  const routeSegment = segments[0] === "en" || segments[0] === "ja" ? segments[1] : segments[0]
  const page = routeSegment || "index"

  if (page === "careers") return "careers"
  if (page === "calendar") return "calendar"
  if (page === "company") return "company"
  if (page === "contact") return "contact"
  if (page === "news") return "news"
  if (page === "work") return "work"
  if (page === "index") return "index"

  return page.replace(/[^a-z0-9_-]/gi, "-")
}

function RootDocument({
  children,
  locale,
  pageClass,
}: Readonly<{ children: ReactNode; locale: "en" | "ja"; pageClass: string }>) {
  return (
    <html
      lang={locale}
      className={pageClass === "index" ? "index" : undefined}
    >
      <head>
        <title>PLAYBRAIN Inc.</title>
        <HeadContent />
        <script dangerouslySetInnerHTML={{ __html: analyticsScript }} />
        <script dangerouslySetInnerHTML={{ __html: remoteImageSkeletonScript }} />
        <script dangerouslySetInnerHTML={{ __html: viewTransitionRouterScript }} />
      </head>
      <body className={pageClass}>
        {children}
        <Scripts />
      </body>
    </html>
  )
}

function NotFound() {
  return (
    <SiteShell
      locale="ja"
      page="error404"
    >
      <ErrorPage locale="ja" />
    </SiteShell>
  )
}
